import type { AuthProvider } from '@communities/community/domain';
import { Text, Badge, Dialog } from '@soteria/design-system-ve';

import * as styles from './AuthenticationProviderDialog.css';

interface AuthenticationProviderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  provider: AuthProvider | null;
}

export function AuthenticationProviderDialog({ isOpen, onClose, provider }: AuthenticationProviderDialogProps) {
  if (!provider) {
    return null;
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xl" title="Authentication Provider Details" onClose={onClose}>
      <div className={styles.container}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.providerInfo}>
            {provider.logoUrl && <img alt={`${provider.name} logo`} className={styles.logo} src={provider.logoUrl} />}
            <div>
              <Text className={styles.providerName}>{provider.name}</Text>
              <div className={styles.badges}>
                <Badge text={provider.providerStatus} />
                {provider.isDefaultProvider && <Badge text="Default Provider" />}
              </div>
            </div>
          </div>
        </div>

        {/* Configuration Details */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>Configuration</Text>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Client ID</Text>
              <Text className={styles.value}>{provider.clientId}</Text>
            </div>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Response Type</Text>
              <Text className={styles.value}>{provider.responseType}</Text>
            </div>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Auth Server URL</Text>
              <Text className={styles.value}>{provider.authServerUrl}</Text>
            </div>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Token Server URL</Text>
              <Text className={styles.value}>{provider.tokenServerUrl}</Text>
            </div>
            {provider.providerUrl && (
              <div className={styles.detailItem}>
                <Text className={styles.label}>Provider URL</Text>
                <Text className={styles.value}>{provider.providerUrl}</Text>
              </div>
            )}
            {provider.jwksUri && (
              <div className={styles.detailItem}>
                <Text className={styles.label}>JWKS URI</Text>
                <Text className={styles.value}>{provider.jwksUri}</Text>
              </div>
            )}
          </div>
        </div>

        {/* Scopes Section */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>Scopes</Text>
          <div className={styles.scopesContainer}>
            {provider.scopes.map(scope => (
              <Badge key={scope} className={styles.scopeBadge} text={scope} />
            ))}
          </div>
        </div>

        {/* Audit Information */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>Audit Information</Text>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Created By</Text>
              <Text className={styles.value}>{provider.createdBy}</Text>
            </div>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Created At</Text>
              <Text className={styles.value}>{formatDate(provider.createdAt)}</Text>
            </div>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Last Modified By</Text>
              <Text className={styles.value}>{provider.lastModifiedBy}</Text>
            </div>
            <div className={styles.detailItem}>
              <Text className={styles.label}>Last Modified At</Text>
              <Text className={styles.value}>{formatDate(provider.lastModifiedAt)}</Text>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
